import apiClient from "../../helpers/apiClient";
import { getBookingSiteOrigin } from "./bookingDomains";

/** True when this company takes card payments through Sola rather than Stripe. */
export const isSolaGateway = (companyData) => companyData?.defaultPaymentGateway === "sola";

/** One key per payment attempt; the backend replays the first outcome for a key it has seen. */
export const newIdempotencyKey = () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `sola-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const companyHeaders = (companyId) => (companyId ? { headers: { "company-id": companyId } } : undefined);

const unwrap = (data, fallback) => {
    if (!data?.success) {
        throw new Error(data?.message || fallback);
    }
    return data?.data;
};

/**
 * Charges a card now. Sola answers immediately, so unlike Stripe there is no redirect: the
 * backend records the payment and runs the post-payment steps before this resolves.
 */
export const chargeSolaReservation = async ({
    reservationId,
    reservationProcess,
    amount,
    idempotencyKey,
    sut,
    xCVV,
    xExp,
    customerEmail,
    extensionDropoffDate,
    companyId,
}) => {
    const { data } = await apiClient.post(
        "/api/sola-payment/reservation-payment",
        {
            reservationId,
            reservationProcess,
            amount,
            idempotencyKey,
            sut,
            xExp,
            ...(xCVV ? { xCVV } : {}),
            ...(customerEmail ? { customerEmail } : {}),
            ...(extensionDropoffDate ? { extensionDropoffDate } : {}),
        },
        companyHeaders(companyId),
    );
    return unwrap(data, "The payment could not be completed.");
};

/**
 * Emails the customer a Sola payment link.
 *
 * The link opens the pay page on the company's own booking site: the booking site picks the
 * company from its domain, so the domain has to be that company's. It is looked up in the
 * copied domain map; a company with no mapped domain cannot be sent a link yet.
 */
export const sendSolaPaymentLink = async ({
    reservationId,
    reservationProcess,
    amount,
    customerEmail,
    companyId,
    extensionDropoffDate,
    successView,
    insuranceName,
}) => {
    const origin = getBookingSiteOrigin(companyId);
    if (!origin) {
        throw new Error(
            "This company has no booking site domain set up yet, so a payment link can't be sent. Add its domain to the domain map.",
        );
    }

    const { data } = await apiClient.post(
        "/api/sola-payment/payment-link",
        {
            reservationId,
            reservationProcess,
            amount,
            customerEmail: customerEmail?.trim(),
            origin,
            ...(extensionDropoffDate ? { extensionDropoffDate } : {}),
            ...(successView ? { successView } : {}),
            ...(insuranceName ? { insuranceName } : {}),
        },
        companyHeaders(companyId),
    );
    return unwrap(data, "Failed to send payment link");
};
